import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, SpinnerGap } from '@phosphor-icons/react';

const LABELS = {
  twitter: 'X (Twitter)', meta: 'Instagram + Facebook', meta_ads: 'Meta Ads', tiktok: 'TikTok', tiktok_ads: 'TikTok Ads', google: 'YouTube', google_ads: 'Google Ads',
};

export default function SocialCallback({ provider }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ status: 'loading' });
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const code = provider === 'tiktok_ads'
      ? (params.get('auth_code') || params.get('code'))
      : params.get('code');
    const st = params.get('state');
    const err = params.get('error_description') || params.get('error');
    if (err) { setState({ status: 'error', message: err }); return; }
    if (!code || !st) { setState({ status: 'error', message: 'Missing authorization code' }); return; }
    api.post(`/social/${provider}/callback`, { code, state: st })
      .then(r => setState({ status: 'ok', account: r.data.account }))
      .catch(e => setState({ status: 'error', message: e.response?.data?.detail || 'Connection failed' }));
  }, [params, provider]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div data-testid={`social-callback-${provider}`} className="w-full max-w-md border border-zinc-800 rounded-2xl bg-zinc-900/50 p-8 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">TuneAmplifier · {LABELS[provider]}</div>

        {state.status === 'loading' && (
          <div className="mt-6">
            <SpinnerGap size={34} className="text-[#ccff00] mx-auto animate-spin" />
            <p className="mt-4 text-sm text-zinc-400 font-mono">Linking your account…</p>
          </div>
        )}

        {state.status === 'ok' && (
          <div className="mt-6" data-testid="social-connect-success">
            <CheckCircle size={40} weight="fill" className="text-[#ccff00] mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-4">Connected.</h1>
            <p className="text-sm text-zinc-400 mt-2">
              {state.account?.name || state.account?.handle
                ? <>Posting as <span className="text-zinc-100 font-medium">{state.account?.handle || state.account?.name}</span>.</>
                : 'Your account is linked.'}
            </p>
            {state.account?.instagram_ready === false && (
              <p className="text-xs font-mono text-amber-400 mt-3">
                No Instagram Business account is linked to that Page — Facebook posting works, Instagram won't until you link one.
              </p>
            )}
            <Button data-testid="social-goto-connections" onClick={() => nav('/connections')} className="btn-neon rounded-full mt-6 h-11 px-6 font-display font-semibold">
              Back to Connections
            </Button>
          </div>
        )}

        {state.status === 'error' && (
          <div className="mt-6" data-testid="social-connect-error">
            <XCircle size={40} weight="fill" className="text-red-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-4">Couldn't connect</h1>
            <p className="text-sm text-zinc-400 mt-2 break-words">{state.message}</p>
            <Button data-testid="social-retry-btn" onClick={() => nav('/connections')} className="rounded-full mt-6 h-11 px-6 border border-zinc-800 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:border-[#ccff00]/60 font-display">
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
